import { DefaultButton, DetailsList, IColumn, Icon, Link, MessageBar, SelectionMode, Spinner } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { EntityAPI, EntityType, IDocumentSearchManagedProperty, IUserConfigPackage } from '../../../API';
import ManagedPropertyEditor, { ManagedPropertyEditorState } from './ManagedPropertyEditor';

export class ManagedPropertiesListState {
	
	public userConfig: IUserConfigPackage;

	public loading: boolean = false;
	public loaded: boolean = false;

	public editState: ManagedPropertyEditorState;
	
	public ids: string[] = [];
	
	update?: (ids: string[]) => void;
	
	items: IDocumentSearchManagedProperty[] = [];

	constructor(userConfig: IUserConfigPackage, update?:(ids:string[]) => void) {
		this.userConfig = userConfig;
		this.update = update;
		
		this.editState = new ManagedPropertyEditorState(this.userConfig, this.delete.bind(this), this.save.bind(this));

		makeAutoObservable(this);
	}
	
	public setSelected(ids:string[]) {
		this.ids = ids || [];
	}
	
	public async load(): Promise<void> {
		this.loading = true;

		let api = await this.getApi();
		let items = await api.all();
		this.setItems(items.items);
	}

	private async getApi() : Promise<EntityAPI<IDocumentSearchManagedProperty>> {
		return await EntityAPI.fromEntityType(
			EntityType.DocumentSearchManagedProperty,
			this.userConfig,
		) as EntityAPI<IDocumentSearchManagedProperty>;
	}

	ensureInitialLoaded() {
		if (!this.loading && !this.loaded) {
			this.load();
		}
	}

	setItems(items: IDocumentSearchManagedProperty[]) {
		this.loading = false;
		this.loaded = true;
		this.items = items;
	}
	
	edit(item: IDocumentSearchManagedProperty) {
		if(this.editState) {
			this.editState.setItem(item);
			this.editState.dialog.setVisible(true);
		}
	}
	
	public showAddNewDialog() {

		if(this.editState) {
			var newItem = {
				Scope: '',
				Title: 'New managed property'
			} as any as IDocumentSearchManagedProperty;

			this.editState.setItem(newItem);
			this.editState.dialog.setVisible(true);
		}
	}

	public async save(original:IDocumentSearchManagedProperty, updated:IDocumentSearchManagedProperty) : Promise<void> {
		
		//Update the original with the new properties
		Object.assign(original, updated);

		if(!original.Id) {
			//There was no id - it needs added to the list
			this.items.push(original);
		}

		let api = await this.getApi();
		original.Id = await api.upsert(updated, []);

		this.updated();
	}

	public async delete(item:IDocumentSearchManagedProperty) {
		this.remove(item);
		let api = await this.getApi();
		await api.delete([item.Id]);
	}

	public remove(item:IDocumentSearchManagedProperty) {
		this.items = this.items.filter(p => p !== item);
		this.updated();
	}
	
	public updated() {
		//Force a rerender
		this.items = this.items.slice();

		//Notify of new values
		if(this.update) {
			this.update(this.items.map(p => p.Id));
		}
	}
}

function ManagedPropertiesList({ state }: { state: ManagedPropertiesListState }) {
	useEffect(() => {
		state.ensureInitialLoaded();
	}, [state]);

	let columns: IColumn[] = [
		{
			key: 'name',
			name: 'Name',
			minWidth: 250,
			maxWidth: 250,
			onRender: (item: IDocumentSearchManagedProperty) => <Link onClick={() => {
				state.edit(item);
			}}>{item.Title}</Link>,
		},
		{
			key: 'internalName',
			name: 'Internal Name',
			minWidth: 40,
			onRender: (item: IDocumentSearchManagedProperty) => <span>
				<span>{item.InternalName}</span>
			</span>,
		},
		{
			key: 'delete',
			name: '',
			minWidth: 100,
			onRender: (item: IDocumentSearchManagedProperty) => <Icon style={{fontSize:20, cursor:'pointer'}} iconName={'Delete'} onClick={() => { state.delete(item) }} />,
		}
	];


	if (state.loading) {
		return <Spinner></Spinner>;
	}

	
	let searchPropsUrl = state.userConfig.tenantUrl.replace(
		'.sharepoint.com',
		'-admin.sharepoint.com',
	) + '/_layouts/15/searchadmin/ta_listmanagedproperties.aspx?level=tenant';

	
	return (
		<>
			<>
				{state.editState && <ManagedPropertyEditor state={state.editState} /> }
				
				<MessageBar>
					Configure search managed properties to appear in document search results.  <Link href={searchPropsUrl} target="_blank" underline>View available properties</Link>
				</MessageBar>
					
				{(state?.items && state.items.length) ?
					<DetailsList selectionMode={SelectionMode.none} columns={columns} items={state?.items || []} /> : 
					<></>
				}
			</>

			<div style={{marginTop:20}}>
				<DefaultButton onClick={state.showAddNewDialog.bind(state)}>Add</DefaultButton>
			</div>
		</>
	);
}

export default observer(ManagedPropertiesList);
